import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { apiClient, createProjectHeaders, formatP0Error, formatScopeAuthorityError, isContextIntegrityError, SCOPE_AUTHORITY_ERROR_CODES } from '../lib/api';
import { handleActionError } from '../lib/errorHandlers';
import { useI18n } from '../contexts/I18nContext';
import { ArrowLeft, Save, MessageSquare, Send, CheckCircle2, AlertCircle, XCircle, Sparkles, Image as ImageIcon, Video, Ban, Rocket, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { fr, enUS, es, de } from 'date-fns/locale';

export default function ContentEditor() {
  const navigate = useNavigate();
  const { projectId, contentId } = useParams();
  const { t, language } = useI18n();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [userMessage, setUserMessage] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [imageGenerating, setImageGenerating] = useState(false);
  const [prediction, setPrediction] = useState(null);
  const [predictorLoading, setPredictorLoading] = useState(false);
  const [applyingSuggestion, setApplyingSuggestion] = useState(null);
  const [publishing, setPublishing] = useState(false);
  
  // Get locale for date-fns
  const getLocale = () => {
    const locales = { fr, en: enUS, es, de };
    return locales[language] || fr;
  };

  useEffect(() => {
    fetchContent();
  }, [contentId]);
  
  // Fetch prediction when content loads or changes
  useEffect(() => {
    if (content) {
      fetchPrediction();
    }
  }, [content?.text, content?.hashtags, content?.cta]);

  const fetchContent = async () => {
    try {
      const response = await apiClient.get(`/contents/${contentId}`);
      setContent(response.data);
    } catch (error) {
      toast.error(t('errorLoading'));
    } finally {
      setLoading(false);
    }
  };
  
  const fetchPrediction = async () => {
    setPredictorLoading(true);
    try {
      const response = await apiClient.post(`/predictor/analyze/${contentId}`);
      setPrediction(response.data);
    } catch (error) {
      console.log('Predictor not available or error:', error);
    } finally {
      setPredictorLoading(false);
    }
  };
  
  const handleApplySuggestion = async (suggestion) => {
    if (content.validation_flag === 'validated') {
      toast.error('Passez en brouillon pour appliquer les suggestions');
      return;
    }
    
    setApplyingSuggestion(suggestion.id);
    try {
      const response = await apiClient.post(`/predictor/apply-suggestion/${contentId}`, {
        action_type: suggestion.action_type
      });
      toast.success(`${t('scoreImproved')} +${response.data.improvement} points`);
      
      // Update content with modifications
      setContent({
        ...content,
        ...response.data.modified_content
      });
      
      // Update prediction
      setPrediction(response.data.new_prediction);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorSaving'));
    } finally {
      setApplyingSuggestion(null);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.put(`/contents/${contentId}`, {
        text: content.text,
        image_url: content.image_url,
        video_url: content.video_url,
        validation_flag: content.validation_flag,
        scheduled_date: content.scheduled_date,
        hashtags: content.hashtags,
        cta: content.cta,
        seo_data: content.seo_data
      });
      toast.success('Modifications enregistrées !');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleValidate = async () => {
    setSaving(true);
    try {
      await apiClient.put(`/contents/${contentId}`, {
        validation_flag: 'validated'
      });
      setContent({ ...content, validation_flag: 'validated' });
      toast.success(t('contentValidated'));
    } catch (error) {
      toast.error(t('errorValidation'));
    } finally {
      setSaving(false);
    }
  };

  const handleDisable = async () => {
    setSaving(true);
    try {
      await apiClient.put(`/contents/${contentId}`, {
        validation_flag: 'disabled'
      });
      setContent({ ...content, validation_flag: 'disabled' });
      toast.success(t('contentDisabled') || 'Contenu désactivé');
    } catch (error) {
      toast.error(t('errorSaving'));
    } finally {
      setSaving(false);
    }
  };

  const handleSetDraft = async () => {
    setSaving(true);
    try {
      await apiClient.put(`/contents/${contentId}`, {
        validation_flag: 'draft'
      });
      setContent({ ...content, validation_flag: 'draft' });
      toast.success(t('contentDraft') || 'Contenu remis en brouillon');
    } catch (error) {
      toast.error(t('errorSaving'));
    } finally {
      setSaving(false);
    }
  };

  const handlePublishNow = async () => {
    // P0 HOTFIX: Validate projectId before call
    if (!projectId) {
      toast.error('⚠️ PROJECT_ID_REQUIRED: Sélectionnez un projet');
      return;
    }
    
    if (!window.confirm(t('publishNowConfirm') || 'Publier ce contenu immédiatement ?')) {
      return;
    }
    
    setPublishing(true);
    const toastId = toast.loading(t('publishing') || 'Publication en cours...');
    
    try {
      // P0 HOTFIX: Add project headers
      const response = await apiClient.post(`/contents/${contentId}/publish`, {}, {
        headers: createProjectHeaders(projectId)
      });
      toast.dismiss(toastId);
      
      if (response.data.success) {
        toast.success(t('publishedSuccess') || 'Contenu publié avec succès !');
        // Update content with published status and current time
        const now = new Date().toISOString();
        setContent({ 
          ...content, 
          validation_flag: 'published',
          published: true,
          published_at: now,
          scheduled_date: now
        });
      } else {
        // Handle specific blocking reasons with actionable messages
        const blockingReason = response.data.blocking_reason;
        const actionCta = response.data.action_cta;
        const actionRequired = response.data.action_required;
        
        if (blockingReason === 'no_credentials') {
          toast.error(
            <div>
              <p className="font-medium">Instagram n'est pas connecté</p>
              <p className="text-sm opacity-80">Allez dans Paramètres pour connecter votre compte.</p>
            </div>,
            { duration: 6000 }
          );
        } else if (blockingReason === 'auto_publish_disabled') {
          toast.error(
            <div>
              <p className="font-medium">Publication automatique désactivée</p>
              <p className="text-sm opacity-80">Instagram est connecté mais la publication auto est désactivée.</p>
            </div>,
            { 
              duration: 8000,
              action: {
                label: actionCta || 'Activer',
                onClick: () => navigate(`/project/${projectId}/settings`)
              }
            }
          );
        } else if (blockingReason === 'needs_reconnect') {
          toast.error(
            <div>
              <p className="font-medium">Token Instagram expiré</p>
              <p className="text-sm opacity-80">Reconnectez votre compte dans les paramètres.</p>
            </div>,
            { duration: 6000 }
          );
        } else if (response.data.auto_repaired) {
          // Config was auto-repaired, retry
          toast.info('Configuration corrigée automatiquement. Réessayez.');
        } else {
          // P0 HOTFIX: Display actual error_code if available
          const errorCode = response.data.error_code;
          const message = response.data.message || t('publishError');
          if (errorCode) {
            toast.error(`${errorCode}: ${message}`);
          } else {
            toast.error(message);
          }
        }
      }
    } catch (error) {
      toast.dismiss(toastId);
      
      // P0 PRODUCTION-GRADE: Gestionnaire centralisé basé sur signaux backend
      // Logique STRICTE: is_actionable, transient_hint, error_reason
      handleActionError(error, toast, { context: 'PUBLISH_NOW' });
      console.error('[PUBLISH_NOW] Error:', error.response?.data || error.message);
    } finally {
      setPublishing(false);
    }
  };

  const handleAIChat = async () => {
    if (!userMessage.trim()) return;
    
    const newUserMsg = { role: 'user', content: userMessage };
    setChatMessages([...chatMessages, newUserMsg]);
    setUserMessage('');
    setChatLoading(true);

    try {
      const response = await apiClient.post('/ai/chat', {
        content_id: contentId,
        project_id: projectId,
        user_message: userMessage,
        context: { current_text: content.text }
      });
      
      const aiMsg = { role: 'assistant', content: response.data.response };
      setChatMessages([...chatMessages, newUserMsg, aiMsg]);
    } catch (error) {
      toast.error('Erreur lors de la communication avec l\'IA');
    } finally {
      setChatLoading(false);
    }
  };

  const handleSearchRealPhotos = async () => {
    setImageGenerating(true);
    try {
      // Extract keywords from content
      const keywords = content.text
        .substring(0, 200)
        .split(' ')
        .filter(w => w.length > 4)
        .slice(0, 5)
        .join(' ');
      
      const response = await apiClient.post('/images/search', {
        query: keywords || 'business marketing',
        text_context: content.text,
        platform: content.platform
      });

      if (response.data.images && response.data.images.length > 0) {
        // Use the top scored image
        const topImage = response.data.images[0];
        const newImageUrl = topImage.url;
        
        // Update local state
        setContent({ ...content, image_url: newImageUrl });
        
        // Save to database immediately
        await apiClient.put(`/contents/${contentId}`, {
          image_url: newImageUrl
        });
        
        toast.success(`Photo réelle de ${topImage.photographer} (${topImage.source}) ✨`);
      } else if (response.data.fallback_available) {
        // Fallback to AI generation
        toast.info('Aucune photo trouvée, génération IA...');
        await handleGenerateImageFallback();
      }
    } catch (error) {
      toast.error('Erreur lors de la recherche de photos');
    } finally {
      setImageGenerating(false);
    }
  };

  const handleGenerateImageFallback = async () => {
    try {
      // IMPORTANT: NO TEXT in the generated image - text should be in the post caption
      const prompt = `Create a clean, professional marketing photo without any text or words. 
      Style: modern, bright, high quality stock photo. 
      Subject: ${content.text.substring(0, 80)}. 
      NO text overlays, NO words, NO letters on the image.`;
      
      const response = await apiClient.post('/ai/generate-image', { prompt });
      const imageUrl = `data:image/png;base64,${response.data.image_base64}`;
      
      // Update local state
      setContent({ ...content, image_url: imageUrl });
      
      // Save to database immediately
      await apiClient.put(`/contents/${contentId}`, {
        image_url: imageUrl
      });
      
      toast.success('Image IA générée avec succès !');
    } catch (error) {
      toast.error('Erreur lors de la génération de l\'image');
    }
  };

  const handleEnhanceImage = async () => {
    if (!content.image_url) {
      toast.error('Aucune image à améliorer');
      return;
    }

    setImageGenerating(true);
    try {
      const response = await apiClient.post('/images/enhance', {
        image_url: content.image_url,
        platform: content.platform
      });
      
      const enhancedUrl = `data:image/jpeg;base64,${response.data.image_base64}`;
      
      // Update local state
      setContent({ ...content, image_url: enhancedUrl });
      
      // Save to database immediately
      await apiClient.put(`/contents/${contentId}`, {
        image_url: enhancedUrl
      });
      
      toast.success('Image améliorée ! (recadrage + contraste + netteté)');
    } catch (error) {
      toast.error('Erreur lors de l\'amélioration');
    } finally {
      setImageGenerating(false);
    }
  };

  const handleDeleteContent = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce contenu ?')) {
      return;
    }

    try {
      await apiClient.delete(`/contents/${contentId}`);
      toast.success('Contenu supprimé avec succès');
      navigate(`/projects/${projectId}/calendar`);
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  if (loading || !content) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  const getValidationBadge = () => {
    switch (content.validation_flag) {
      case 'validated':
        return <Badge className="bg-success/10 text-success border-success/20 gap-1"><CheckCircle2 className="w-3 h-3" />{t('validated')}</Badge>;
      case 'draft':
        return <Badge className="bg-warning/10 text-warning border-warning/20 gap-1"><AlertCircle className="w-3 h-3" />{t('draft')}</Badge>;
      case 'disabled':
        return <Badge className="bg-error/10 text-error border-error/20 gap-1"><XCircle className="w-3 h-3" />{t('disabled')}</Badge>;
      default:
        return <Badge variant="outline">{t('draft')}</Badge>;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-slate-50">
      <header className="border-b bg-white/70 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate(`/projects/${projectId}/calendar`)} data-testid="back-to-calendar-button">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-xl font-heading font-bold">Édition du contenu</h1>
                  {getValidationBadge()}
                </div>
                <p className="text-sm text-muted-foreground">
                  {content.platform} • {content.content_type} • {format(new Date(content.scheduled_date), 'd MMMM yyyy HH:mm', { locale: getLocale() })}
                  {content.published && <span className="text-green-600 ml-2">✓ Publié</span>}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Status change buttons */}
              {content.validation_flag === 'disabled' && (
                <Button onClick={handleSetDraft} variant="outline" className="rounded-full gap-2" data-testid="set-draft-button">
                  <AlertCircle className="w-4 h-4" />
                  {t('draft')}
                </Button>
              )}
              {content.validation_flag !== 'disabled' && (
                <Button onClick={handleDisable} variant="outline" className="rounded-full gap-2 text-error border-error/30 hover:bg-error/10" data-testid="disable-content-button">
                  <Ban className="w-4 h-4" />
                  {t('disable') || 'Désactiver'}
                </Button>
              )}
              {content.validation_flag !== 'validated' && content.validation_flag !== 'disabled' && (
                <Button onClick={handleValidate} variant="outline" className="rounded-full gap-2 text-success border-success/30 hover:bg-success/10" data-testid="validate-content-button">
                  <CheckCircle2 className="w-4 h-4" />
                  {t('validate')}
                </Button>
              )}
              {content.validation_flag === 'validated' && (
                <Button onClick={handleSetDraft} variant="outline" className="rounded-full gap-2" data-testid="set-draft-button">
                  <AlertCircle className="w-4 h-4" />
                  {t('draft')}
                </Button>
              )}
              <Button onClick={handleSave} disabled={saving} className="rounded-full gap-2" data-testid="save-content-button">
                <Save className="w-4 h-4" />
                {saving ? t('saving') : t('save')}
              </Button>
              <Button 
                onClick={handlePublishNow} 
                disabled={publishing || content.validation_flag === 'disabled'} 
                className="rounded-full gap-2 bg-gradient-to-r from-primary to-purple-600 hover:from-primary/90 hover:to-purple-600/90"
                data-testid="publish-now-button"
              >
                {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
                {t('publishNow') || 'Poster maintenant'}
              </Button>
              <Button onClick={handleDeleteContent} variant="ghost" size="icon" className="text-error hover:bg-error/10" data-testid="delete-content-button">
                <XCircle className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Content Editor */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading">Contenu</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="text">Texte</Label>
                  <Textarea
                    id="text"
                    value={content.text}
                    onChange={(e) => setContent({ ...content, text: e.target.value })}
                    rows={8}
                    className="resize-none"
                    data-testid="content-text-input"
                  />
                  <p className="text-xs text-muted-foreground">{content.text.length} caractères</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="hashtags">Hashtags</Label>
                  <Input
                    id="hashtags"
                    value={content.hashtags.join(', ')}
                    onChange={(e) => setContent({ ...content, hashtags: e.target.value.split(',').map(h => h.trim()) })}
                    placeholder="#marketing, #ai, #automation"
                    data-testid="content-hashtags-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cta">Call-to-Action</Label>
                  <Input
                    id="cta"
                    value={content.cta || ''}
                    onChange={(e) => setContent({ ...content, cta: e.target.value })}
                    placeholder="Ex: Découvrez plus sur notre site"
                    data-testid="content-cta-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="scheduled_date">Date de publication</Label>
                  <Input
                    id="scheduled_date"
                    type="datetime-local"
                    value={format(new Date(content.scheduled_date), "yyyy-MM-dd'T'HH:mm")}
                    onChange={(e) => setContent({ ...content, scheduled_date: new Date(e.target.value).toISOString() })}
                    data-testid="content-date-input"
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading">Médias</CardTitle>
                  <div className="flex gap-2">
                    <Button 
                      onClick={handleSearchRealPhotos} 
                      disabled={imageGenerating} 
                      variant="default" 
                      size="sm" 
                      className="rounded-full gap-2" 
                      data-testid="search-photos-button"
                    >
                      <ImageIcon className="w-4 h-4" />
                      {imageGenerating ? 'Recherche...' : 'Changer de photo'}
                    </Button>
                    {content.image_url && (
                      <Button 
                        onClick={handleEnhanceImage} 
                        disabled={imageGenerating} 
                        variant="outline" 
                        size="sm" 
                        className="rounded-full gap-2" 
                        data-testid="enhance-image-button"
                      >
                        <Sparkles className="w-4 h-4" />
                        Améliorer
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 mb-4">
                  <p className="text-xs text-muted-foreground">
                    💡 <strong>Astuce :</strong> Le texte marketing est dans votre post, pas dans l'image. 
                    Les images sans texte génèrent plus d'engagement.
                  </p>
                </div>

                {content.image_url && (
                  <div className="space-y-2">
                    <div className="border rounded-lg overflow-hidden">
                      <img src={content.image_url} alt="Content visual" className="w-full h-auto" data-testid="content-image-preview" />
                    </div>
                    <p className="text-xs text-success flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" />
                      Photos réelles optimisées pour les réseaux sociaux
                    </p>
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="image_url">URL de l'image</Label>
                  <Input
                    id="image_url"
                    value={content.image_url || ''}
                    onChange={(e) => setContent({ ...content, image_url: e.target.value })}
                    placeholder="https://..."
                    data-testid="content-image-url-input"
                  />
                </div>

                {content.content_type === 'video' && (
                  <div className="space-y-2">
                    <Label htmlFor="video_url">URL de la vidéo</Label>
                    <Input
                      id="video_url"
                      value={content.video_url || ''}
                      onChange={(e) => setContent({ ...content, video_url: e.target.value })}
                      placeholder="https://..."
                      data-testid="content-video-url-input"
                    />
                    <p className="text-xs text-warning flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      Les vidéos nécessitent une validation humaine avant publication
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar: Predictor + AI Chat */}
          <div className="lg:col-span-1 space-y-4">
            {/* Performance Predictor */}
            <Card className="border-2 border-primary/20">
              <CardHeader className="border-b bg-primary/5">
                <CardTitle className="font-heading flex items-center gap-2 text-base">
                  🎯 {t('performancePredictor')}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                {predictorLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                  </div>
                ) : prediction?.error ? (
                  <div className="text-center py-4">
                    <p className="text-sm text-muted-foreground">{t('premiumFeature')}</p>
                    <p className="text-xs text-muted-foreground mt-1">{prediction.message}</p>
                  </div>
                ) : prediction ? (
                  <div className="space-y-4">
                    {/* Score display */}
                    <div className="text-center">
                      <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full text-2xl font-bold ${
                        prediction.level === 'high' ? 'bg-success/20 text-success' :
                        prediction.level === 'medium' ? 'bg-warning/20 text-warning' :
                        'bg-error/20 text-error'
                      }`}>
                        {prediction.score}
                      </div>
                      <p className={`text-sm font-medium mt-2 ${
                        prediction.level === 'high' ? 'text-success' :
                        prediction.level === 'medium' ? 'text-warning' :
                        'text-error'
                      }`}>
                        {prediction.level === 'high' ? t('highPotential') :
                         prediction.level === 'medium' ? t('mediumPotential') :
                         t('lowPotential')}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">{prediction.message}</p>
                    </div>
                    
                    {/* Score breakdown */}
                    {prediction.breakdown && (
                      <div className="space-y-2 pt-2 border-t">
                        {Object.entries(prediction.breakdown).map(([key, value]) => (
                          <div key={key} className="flex items-center justify-between text-xs">
                            <span className="text-muted-foreground capitalize">{key}</span>
                            <div className="flex items-center gap-2">
                              <div className="w-20 h-1.5 bg-muted rounded-full overflow-hidden">
                                <div 
                                  className={`h-full rounded-full ${
                                    value >= 70 ? 'bg-success' : value >= 50 ? 'bg-warning' : 'bg-error'
                                  }`}
                                  style={{ width: `${value}%` }}
                                />
                              </div>
                              <span className="w-8 text-right">{value}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Suggestions */}
                    {prediction.suggestions && prediction.suggestions.length > 0 && (
                      <div className="space-y-2 pt-2 border-t">
                        <p className="text-xs font-medium">{t('suggestions')}</p>
                        {prediction.suggestions.map((suggestion) => (
                          <div key={suggestion.id} className="flex items-start gap-2 p-2 bg-muted/50 rounded-lg">
                            <span className="text-xs flex-1">{suggestion.text}</span>
                            {suggestion.auto_applicable && content.validation_flag !== 'validated' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-6 text-xs px-2"
                                onClick={() => handleApplySuggestion(suggestion)}
                                disabled={applyingSuggestion === suggestion.id}
                              >
                                {applyingSuggestion === suggestion.id ? '...' : t('applySuggestion')}
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    {t('loading')}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* AI Chat Assistant */}
            <Card className="h-[calc(100vh-450px)] flex flex-col">
              <CardHeader className="border-b">
                <CardTitle className="font-heading flex items-center gap-2 text-base">
                  <Sparkles className="w-5 h-5 text-primary" />
                  {t('aiAssistant')}
                </CardTitle>
                <p className="text-xs text-muted-foreground">{t('aiAssistantDesc')}</p>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col p-4 overflow-hidden">
                <div className="flex-1 overflow-y-auto space-y-3 mb-4" data-testid="ai-chat-messages">
                  {chatMessages.length === 0 ? (
                    <div className="text-center py-8">
                      <MessageSquare className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                      <p className="text-sm text-muted-foreground">Commencez une conversation</p>
                      <p className="text-xs text-muted-foreground mt-2">Exemples:</p>
                      <div className="space-y-1 mt-2 text-xs text-muted-foreground">
                        <p>• "Rends ce texte plus engageant"</p>
                        <p>• "Adapte pour LinkedIn"</p>
                        <p>• "Raccourcis à 100 caractères"</p>
                      </div>
                    </div>
                  ) : (
                    chatMessages.map((msg, idx) => (
                      <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[80%] rounded-lg p-3 ${msg.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                          <p className="text-sm">{msg.content}</p>
                        </div>
                      </div>
                    ))
                  )}
                  {chatLoading && (
                    <div className="flex justify-start">
                      <div className="bg-muted rounded-lg p-3">
                        <div className="flex gap-1">
                          <div className="w-2 h-2 bg-muted-foreground/30 rounded-full animate-bounce"></div>
                          <div className="w-2 h-2 bg-muted-foreground/30 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                          <div className="w-2 h-2 bg-muted-foreground/30 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex gap-2">
                  <Input
                    value={userMessage}
                    onChange={(e) => setUserMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAIChat()}
                    placeholder="Posez votre question..."
                    disabled={chatLoading}
                    data-testid="ai-chat-input"
                  />
                  <Button onClick={handleAIChat} disabled={chatLoading || !userMessage.trim()} size="icon" className="rounded-full" data-testid="ai-chat-send-button">
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}